import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useAppConfig } from '../ConfigContext';
import LoadingSpinner from './LoadingSpinner';
import '../index.css';

function ArenaLeaderboard({
  user,
  onNavigate,
  openedVia = "menu",
  initialView,
  arenaId: initialArenaId,
  arenaIndex: initialArenaIndex,
  viewOnly = false,
}) {
  const { season: activeSeasonObj } = useAppConfig();
  const activeSeasonNumber = activeSeasonObj?.season_number ?? null;
  const activeSeasonEndISO = activeSeasonObj?.end_date ?? null;

  // Views for arena leaderboard
  // trophies = primary board, winners = wins board (you can add more later)
  const normalizeView = (v) => {
    const s = String(v || '').toLowerCase();
    if (s === 'wins' || s === 'winners' || s === 'fighters') return 'wins';
    return 'trophies';
  };

  const [view, setView] = useState(() => normalizeView(initialView));
  useEffect(() => { if (initialView) setView(normalizeView(initialView)); }, [initialView]);

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(activeSeasonNumber);

  const [arenas, setArenas] = useState([]);
  const [selectedArena, setSelectedArena] = useState(null);

  const [error, setError] = useState('');

  const [myRank, setMyRank] = useState(null);
  const [myRankLoading, setMyRankLoading] = useState(false);
  const [myRankError, setMyRankError] = useState('');

  // Live "Ends in" for current season
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!activeSeasonEndISO) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [activeSeasonEndISO]);

  const endsIn = useMemo(() => {
    if (!activeSeasonEndISO || selectedSeason !== activeSeasonNumber) return null;
    const end = new Date(activeSeasonEndISO).getTime();
    const ms = end - now;
    if (!Number.isFinite(ms) || ms <= 0) return 'Ended';
    const d = Math.floor(ms / (24 * 3600e3));
    const h = Math.floor((ms % (24 * 3600e3)) / 3600e3);
    const m = Math.floor((ms % 3600e3) / 60e3);
    return `${d}D ${h}H ${m}M`;
  }, [activeSeasonEndISO, now, selectedSeason, activeSeasonNumber]);

  function svgFallback(name = '?') {
    const initial = (name?.[0] || '?').toUpperCase();
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
        <rect width="100%" height="100%" fill="#444"/>
        <text x="50%" y="50%" dominant-baseline="middle"
              text-anchor="middle" font-size="32"
              fill="#fff" font-family="Arial, sans-serif">${initial}</text>
      </svg>`;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

    const truncateUsername = (str, max = 20) => {
    const s = String(str ?? '');
    const chars = Array.from(s);
    return chars.length > max ? chars.slice(0, max).join('') + '…' : s;
  };

  const cosmeticAssetSrc = (value) => {
    const v = String(value || '').trim();
    if (!v) return null;

    if (v.startsWith('/') || v.startsWith('http://') || v.startsWith('https://')) {
      return v;
    }

    return `/name-cosmetics/${v}`;
  };

  const getNameStyleCSS = (styleValue) => {
    const v = String(styleValue || '').trim().toLowerCase();

    if (v === 'gold' || v === 'golden') {
      return {
        backgroundImage: 'linear-gradient(90deg, #fff2a8, #ffcc33, #ff8c00)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
      };
    }

    if (v === 'ice' || v === 'frost') {
      return {
        backgroundImage: 'linear-gradient(90deg, #ffffff, #8eeaff, #2f8cff)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
      };
    }

    if (v === 'toxic' || v === 'green') {
      return {
        backgroundImage: 'linear-gradient(90deg, #dfff7a, #47ff5a, #00b341)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
      };
    }

    if (v === 'fire' || v === 'flame') {
      return {
        backgroundImage: 'linear-gradient(90deg, #fff06a, #ff7a18, #ff1e1e)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
      };
    }

    if (v === 'style.png' || v === 'shadow' || v === 'dark') {
      return {
        backgroundImage: 'linear-gradient(90deg, #d9d2ff, #8b5cff, #2d145c)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
      };
    }

    return {};
  };

  // Seasons list (optional; keep same behavior as your other leaderboard)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/seasons`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json(); // expect { seasons: [4,3,2,1] }
        const arr = Array.isArray(data?.seasons) ? data.seasons.map(Number).filter(Number.isFinite) : [];
        const sorted = [...new Set(arr)].sort((a, b) => b - a);
        if (!cancelled) setSeasons(sorted);
      } catch (e) {
        if (!cancelled && activeSeasonNumber != null) setSeasons([activeSeasonNumber]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSeasonNumber]);

  // Keep selection in sync once we know active season
  useEffect(() => {
    if (activeSeasonNumber != null && selectedSeason == null) {
      setSelectedSeason(activeSeasonNumber);
    }
  }, [activeSeasonNumber, selectedSeason]);

  // Load arenas list
  useEffect(() => {
  let cancelled = false;
  (async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/arenas/arenas`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (Array.isArray(json?.arenas) ? json.arenas : []);
      const cleaned = list
        .map(a => ({ id: String(a?.id ?? a?.arena_id ?? a), name: a?.name ?? a?.title ?? String(a?.id ?? a) }))
        .filter(a => a.id && a.id !== 'null' && a.id !== 'undefined');

      if (!cancelled) setArenas(cleaned);
    } catch {
      if (!cancelled) setArenas([]);
    }
  })();

  return () => { cancelled = true; };
}, []); // ✅ load ONCE

  useEffect(() => {
  if (!arenas.length) return;

  // ✅ Highest priority: arena passed from Arenas screen
  if (initialArenaId) {
    setSelectedArena(String(initialArenaId));
    return;
  }

  // ✅ Fallback ONLY if leaderboard opened standalone
  if (!selectedArena) {
    setSelectedArena(String(arenas[0].id));
  }
}, [arenas, initialArenaId]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [selectedSeason, selectedArena, view, pageSize]);

  const isCurrentUser = (entryId) => String(entryId) === String(user?.id);
  const getRankIcon = (rank) => {
  if (rank === 0) return '/1.png';
  if (rank === 1) return '/2.png';
  if (rank === 2) return '/3.png';
  return `#${rank + 1}`;
};

  const titleSeason = selectedSeason != null ? `SEASON ${selectedSeason}` : 'SEASON';
  const arenaName = useMemo(() => {
    const a = arenas.find(x => String(x.id) === String(selectedArena));
    return a?.name || 'ARENA';
  }, [arenas, selectedArena]);

  // MAIN leaderboard fetch
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const offset = (page - 1) * pageSize;
      if (selectedSeason == null || !selectedArena) { setEntries([]); setLoading(false); return; }

      setLoading(true);
      setError('');

      try {
        const metric = view === 'wins' ? 'wins' : 'trophies';

        let url = `${process.env.REACT_APP_API_URL}/api/arenas/leaderboard?season=${encodeURIComponent(selectedSeason)}&arenaId=${encodeURIComponent(selectedArena)}&metric=${encodeURIComponent(metric)}&limit=${pageSize}&offset=${offset}`;

        let res = await fetch(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        // Optional /:season fallback like your other file
        if (res.status === 404) {
          url = `${process.env.REACT_APP_API_URL}/api/arenas/leaderboard/${encodeURIComponent(selectedSeason)}?arenaId=${encodeURIComponent(selectedArena)}&metric=${encodeURIComponent(metric)}&limit=${pageSize}&offset=${offset}`;
          res = await fetch(url, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        const raw = Array.isArray(json) ? json : (Array.isArray(json?.items) ? json.items : []);
        const totalFromApi = Number(Array.isArray(json) ? 0 : (json?.total ?? 0));

                const cleaned = raw.map(r => ({
          id: r.id ?? r.user_id ?? r.userId,
          username: r.username ?? r.name ?? 'Unknown',
          trophies: Number(r.trophies ?? 0),
          wins: Number(r.wins ?? 0),
          losses: Number(r.losses ?? 0),
          battles: Number(r.battles ?? 0),
          avatar: r.avatarUrl ?? r.avatar_url,
          earlyBird: r.early_bird,
          gamepassActive: r.gamepass_active,

          activeNameStyle: r.active_name_style ?? r.activeNameStyle ?? null,
          activeNameBanner: r.active_name_banner ?? r.activeNameBanner ?? null,
          activeNameEmogy: r.active_name_emogy ?? r.activeNameEmogy ?? null,
        }));

        if (!cancelled) {
          setEntries(cleaned);

          if (totalFromApi) setTotal(totalFromApi);
          else if (!Array.isArray(json) && typeof json?.total === 'number') setTotal(Number(json.total));
          else setTotal(Math.max(total, (page - 1) * pageSize + cleaned.length));
        }
      } catch (e) {
        console.error('arena leaderboard error:', e);
        if (!cancelled) {
          setEntries([]);
          setError('Could not load arena leaderboard.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [selectedSeason, selectedArena, view, page, pageSize]);

  // MY RANK fetch
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (selectedSeason == null || !selectedArena) { setMyRank(null); return; }

      setMyRankLoading(true);
      setMyRankError('');

      try {
        const metric = view === 'wins' ? 'wins' : 'trophies';

        let url = `${process.env.REACT_APP_API_URL}/api/arenas/leaderboard/my-rank?season=${encodeURIComponent(selectedSeason)}&arenaId=${encodeURIComponent(selectedArena)}&metric=${encodeURIComponent(metric)}`;

        let res = await fetch(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        if (res.status === 404) {
          url = `${process.env.REACT_APP_API_URL}/api/arenas/leaderboard/my-rank/${encodeURIComponent(selectedSeason)}?arenaId=${encodeURIComponent(selectedArena)}&metric=${encodeURIComponent(metric)}`;
          res = await fetch(url, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        if (!cancelled) setMyRank(json?.rank != null ? json : { ...json, rank: null });
      } catch (e) {
        if (!cancelled) {
          setMyRank(null);
          setMyRankError('Could not load your rank.');
        }
      } finally {
        if (!cancelled) setMyRankLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedSeason, selectedArena, view]);

  const isTrophies = view === 'trophies';
  const isWins = view === 'wins';

  const amountIcon = isTrophies ? '/v2/trophy.png' : '/wins.png';
  const amountLabel = isTrophies ? 'Trophies' : 'Wins';

  return (
    <div
      className="shop-container" style={{marginTop: -20}}
    >
      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          display: 'none',
          width: '100%',
          gap: 6,
          alignItems: 'center',
          justifyContent: 'space-between',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          className={`bt ${view === 'trophies' ? 'bt_on' : 'bt_off'}`}
          style={{
            backgroundImage: "url('/bt_on.png')",
            backgroundSize: '100% 42px',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center bottom',
            flex: 1,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            minWidth: 120,
          }}
          onClick={() => setView('trophies')}
        >
          TROPHIES
        </div>

        <div
          className={`bt ${view === 'wins' ? 'bt_on' : 'bt_off'}`}
          style={{
            backgroundImage: "url('/bt_on.png')",
            backgroundSize: '100% 42px',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center bottom',
            flex: 1,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            minWidth: 120,
          }}
          onClick={() => setView('wins')}
        >
          FIGHTERS
        </div>
      </div>

     {/* Header + selectors */}
<div className="card-grid" style={{ marginLeft: -10, marginRight: -10, width: 'calc(100% + 20px)' }}>
  <div className="modal_title">
    {arenaName} Top Fighters
  </div>

  <div className="modal_body modal_body_2">
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 10,
        flexWrap: 'wrap',
        width: '100%',
        textAlign: 'center',
        marginBottom: 25,
      }}
    >

          {/* Arena selector (keep visible or hide like season select) */}
          {arenas.length > 0 && (
            <select
              value={selectedArena ?? ''}
              onChange={(e) => setSelectedArena(e.target.value || null)}
              className="form-select"
              style={{ display: "none", borderRadius: "18px", width: 220, height: 36, padding: '4px 8px' }}
            >
              {arenas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}

          {/* Season selector (optional; you can keep hidden like your other leaderboard) */}
          {seasons.length > 0 && (
            <select
              value={selectedSeason ?? ''}
              onChange={(e) => setSelectedSeason(e.target.value ? Number(e.target.value) : null)}
              className="form-select"
              style={{ display: "none", borderRadius: "18px", width: 200, height: 36, padding: '4px 8px' }}
            >
              {seasons.map((s) => (
                <option key={s} value={s}>
                  Season {s}{s === activeSeasonNumber ? ' (current)' : ''}
                </option>
              ))}
            </select>
          )}


        {/* My Rank banner */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 10, marginBottom: 10 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 6,
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid #555',
              color: '#ffcc62',
              textShadow: '1px 1px #000',
              fontSize: 16,
            }}
          >
          {myRankLoading ? (
  <div
    style={{
      minWidth: 180,
      minHeight: 46,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner
      text="Loading Rank..."
      size={30}
    />
  </div>
) : myRankError ? (
              <span>{myRankError}</span>
            ) : myRank?.rank != null ? (
              <>
                <span><b>Your Rank:</b> <span style={{ fontWeight: "bold", color: "#fff" }}>#{myRank.rank}</span></span>
                <span style={{ opacity: 0.8 }}>
                  ({isWins ? `${myRank?.wins ?? 0} Wins` : `${myRank?.trophies ?? 0} Trophies`})
                </span>
              </>
            ) : (
              <span>
                {isWins
                  ? "No rank yet — win at least one arena fight to enter the Fighters board."
                  : "No rank yet — earn trophies in this arena to enter the board."}
              </span>
            )}
          </div>
        </div>

        {selectedSeason === activeSeasonNumber && endsIn && (
          <h2
            style={{
              fontSize: '21px',
              fontWeight: 'normal',
              color: '#fff',
              marginTop: '6px',
              padding: 0,
              textShadow: '1px 1px #000',
              display: 'none'
            }}
          >
            Ends in: {endsIn}
          </h2>
        )}

        {error && (
          <div style={{ color: '#ff8080', marginTop: 10, textShadow: '1px 1px #000' }}>{error}</div>
        )}

       {/* List */}
{loading ? (
  <div
    style={{
      width: "100%",
      minHeight: 320,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    }}
  >
    <LoadingSpinner
      text="Loading Leaderboard..."
      size={56}
    />
  </div>
) : (
  entries.map((entry, idx) => {
            const offset = (page - 1) * pageSize;
            const globalIdx = offset + idx;

            const count = isWins ? entry.wins : entry.trophies;
            const rankLabel = getRankIcon(globalIdx);

            const rankLength = String(rankLabel).length;
            const rankFontSize =
              rankLength > 4 ? 14 :
              rankLength > 3 ? 16 :
              20;

            const bannerSrc = cosmeticAssetSrc(entry.activeNameBanner);
            const emogySrc = cosmeticAssetSrc(entry.activeNameEmogy);
            const nameStyle = getNameStyleCSS(entry.activeNameStyle);

            return (
              <motion.div
  key={entry.id ?? idx}
  className="task-card"
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ duration: 0.3, delay: idx * 0.05 }}                
                style={{
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",

  display: "flex",
  alignItems: "center",
  gap: 10,

  paddingTop: 10,
  paddingBottom: 10,
  paddingLeft: 18,
  paddingRight: 21,

  cursor: "pointer",
  margin: 0,
}}
              >
                {/* Rank */}
<span
  style={{
    minWidth: 32,
    width: 32,
    height: 32,
    padding: '0 4px',
    textAlign: 'center',
    fontSize: rankFontSize,
    color: '#fff',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  }}
>
  {globalIdx <= 2 ? (
    <img
      src={rankLabel}
      alt={`Rank ${globalIdx + 1}`}
      style={{
        width: 32,
        height: 32,
        objectFit: 'contain',
        display: 'block',
      }}
      draggable={false}
    />
  ) : (
    rankLabel
  )}
</span>

                {/* Avatar + username */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <b
                    style={{
                      lineHeight: '32px',
                      borderRadius: '8px',
                      display: 'inline-block',
                      backgroundColor: '#000',
                      width: '32px',
                      height: '32px',
                      overflow: 'hidden',
                      position: 'relative',
                      flex: '0 0 32px',
                    }}
                  >
                    <img
                      src={entry.avatar || svgFallback(entry.username)}
                      alt=""
                      style={{
                        border: entry?.earlyBird ? '1px solid #FFD700' : '0px',
                        borderRadius: '8px',
                        boxSizing: 'border-box',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                      loading="lazy"
                    />
                    {entry?.earlyBird && (
                      <img
                        src="./whitelist.png"
                        style={{ bottom: '1px', right: '1px', position: 'absolute', width: '15px', height: '15px' }}
                        title="Whitelisted"
                      />
                    )}
                    {entry?.gamepassActive && (
                      <img
                        src="/passicon.png"
                        style={{
                          bottom: '1px',
                          left: '1px',
                          position: 'absolute',
                          width: '15px',
                          height: '15px'
                        }}
                        title="Gamepass Active"
                      />
                    )}
                  </b>

                                    <span
                    style={{
                      fontSize: '18px',
                      color: entry.gamepassActive
                        ? '#EA5702'
                        : isCurrentUser(entry.id)
                          ? '#fff'
                          : '#ffcc62',
                      textShadow: '1px 1px #000',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      width: '100%',
                      ...nameStyle,
                    }}
                    title={entry.username}
                  >
                    {emogySrc && (
                      <img
                        src={emogySrc}
                        alt=""
                        draggable={false}
                        style={{
                          width: 22,
                          height: 22,
                          objectFit: 'contain',
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {truncateUsername(entry.username)}
                    </span>
                  </span>
                </div>

                {/* Amount */}
                <div
                  className="amount"
                  style={{
                    paddingRight: '8px',
                    fontSize: '18px',
                    backgroundImage: `url('${amountIcon}')`,
                    backgroundSize: isWins ? '36px' : '21px 21px',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'left center',
                    paddingLeft: isWins ? '34px' : '26px',
                    color: '#fff',
                    textShadow: '1px 1px #000',
                    flexShrink: 0,
                    marginLeft: '8px',
                  }}
                >
                  {count}
                </div>
              </motion.div>
            );
          })
        )}
      </div>

     {/* Pagination — CardPicker style */}

      <div>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      width: "100%",
    }}
  >
    <button
      type="button"
      disabled={page <= 1}
      onClick={() => setPage((p) => Math.max(1, p - 1))}
      style={{
        width: 42,
        height: 42,
        border: 0,
        padding: 0,
        background: "transparent",
        opacity: page <= 1 ? 0.45 : 1,
        cursor: page <= 1 ? "not-allowed" : "pointer",
      }}
    >
      <img
        src="/v2/left.png"
        alt="Previous"
        draggable={false}
        style={{
          width: 44,
          height: 44,
          objectFit: "contain",
          display: "block",
          pointerEvents: "none",
        }}
      />
    </button>

    <div
      style={{
        height: 42,
        lineHeight: "38px",
        borderRadius: 8,
        background: "rgba(0,0,0,0.45)",
        border: "2px solid #7f5031",
        color: "#fff",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
        textTransform: "uppercase",
        textShadow: "1px 1px #000",
        boxSizing: "border-box",
        whiteSpace: "nowrap",
        fontFamily: '"Fredoka", sans-serif',
        padding: "0 15px",
      }}
    >
      Page {page}{" "}
      <span style={{ color: "#ffdf5f" }}>
        / {totalPages}
      </span>
    </div>

    <button
      type="button"
      disabled={page >= totalPages}
      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      style={{
        width: 42,
        height: 42,
        border: 0,
        padding: 0,
        background: "transparent",
        opacity: page >= totalPages ? 0.45 : 1,
        cursor: page >= totalPages ? "not-allowed" : "pointer",
      }}
    >
      <img
        src="/v2/right.png"
        alt="Next"
        draggable={false}
        style={{
          width: 44,
          height: 44,
          objectFit: "contain",
          display: "block",
          pointerEvents: "none",
        }}
      />
    </button>
  </div>
</div>

  </div>
</div>
</div>
  );
}

export default ArenaLeaderboard;
